import { TIME_RANGES } from "@/types/time-range";
import { getMetrics, getChartIDsFor } from "@/utils/chart";
import { Series, SeriesPoint } from "@/types/common";
import { StatType } from "@/types/stats";

export function generateProcessedStatsState(statType: StatType) {
  const state: { [propName: string]: {} } = {};

  for (const timeRange of TIME_RANGES) {
    const chartIDsObj: { [propName: string]: {} } = {};
    getChartIDsFor(statType).forEach((chartID: string) => {
      chartIDsObj[chartID] = [];
    });
    state[timeRange] = chartIDsObj;
  }

  return state;
}

function findMatchedIndexes(columns: string[], columnsSubset: string[]): number[] {
  const matchedIndexes: number[] = [];

  // Doing `for...of` instead of filtering `columns`, because the order of `columnsSubset` is important
  for (const column of columnsSubset) if (columns.includes(column)) matchedIndexes.push(columns.indexOf(column));

  return matchedIndexes;
}

function mapPointsToMatchedIndexes(series: Series, matchedIndexes: number[]): SeriesPoint[] {
  return series.values.map((point: SeriesPoint) => {
    const mappedPoint: SeriesPoint = [];

    // Doing `for...of` instead of filtering `points`, because the order of `matchedIndexes` is important
    for (const index of matchedIndexes) mappedPoint.push(point[index]);

    return mappedPoint;
  });
}

function isNullPoint(point: SeriesPoint) {
  // Start at 1, skip `time`
  for (let i = 1; i < point.length; i++) {
    if (point[i] !== null) {
      return false;
    }
  }

  return true;
}

function removeFirstNullPoints(points: SeriesPoint[]) {
  for (const point of points) {
    if (isNullPoint(point)) {
      points.shift();
    } else {
      return;
    }
  }
}

function removeLastNullPoints(points: SeriesPoint[]) {
  const pointsReversed = [...points].reverse();
  for (const point of pointsReversed) {
    if (isNullPoint(point)) {
      points.pop();
    } else {
      return;
    }
  }
}

export function trimNullPoints(points: SeriesPoint[]) {
  removeFirstNullPoints(points);
  removeLastNullPoints(points);
}

/**
 * Generate a series group for a chart
 *
 * The `seriesArray` param contains series with all metrics for all charts
 *
 * Generating series for an individual chart means filtering metrics
 * down to only those required by a chart
 */
export function generateChartSeries(seriesArray: Series[], chartID: string) {
  // Columns are the same in each series if they have data,
  // get from the first available
  // 'time' is the first column
  let allColumns: undefined | string[];
  for (const series of seriesArray) {
    if (series.columns) {
      allColumns = [...series.columns];
      break;
    }
  }

  // return an empty series
  if (!allColumns) {
    return [
      {
        columns: [],
        name: "",
        values: [],
      },
    ];
  }

  // Set 'time' as first column
  const chartColumns = ["time"].concat(getMetrics(chartID));

  const matchedIndexes = findMatchedIndexes(allColumns, chartColumns);

  const mappedSeriesGroup = seriesArray.map(series => {
    const points = mapPointsToMatchedIndexes(series, matchedIndexes);
    trimNullPoints(points);

    const mappedSeries: Series = {
      columns: [...chartColumns],
      name: series.name,
      values: points,
    };

    // processed stats have a tag of server
    // introspection stats do not have tags
    if (series.tags) {
      mappedSeries.tags = series.tags;
    }

    return mappedSeries;
  });

  return mappedSeriesGroup;
}

// Calculate and add hit_rate and requests metrics
// for compound and prefix stats
export function appendCalculatedMetricsToSeries(series: Series) {
  // Add calculated columns
  series.columns.push("hit_rate");
  series.columns.push("requests");

  for (const point of series.values) {
    const totalHitsRate = point[series.columns.indexOf("total_hits_rate")];
    const totalMissesRate = point[series.columns.indexOf("total_misses_rate")];
    const totalMutationsRate = point[series.columns.indexOf("total_mutations_rate")];

    // calculate hit rate
    const hitRateRequests = totalHitsRate + totalMissesRate;
    // Default to null (no data), not zero
    let hitRate: null | number = null;
    if (hitRateRequests > 0) {
      hitRate = totalHitsRate / hitRateRequests;
    }
    point.push(hitRate);

    // calculate requests for request fraction
    let requests: null | number = null;
    // Make sure at least value is not null, because summing null returns 0
    if (totalHitsRate !== null || totalMissesRate !== null || totalMutationsRate !== null) {
      requests = totalHitsRate + totalMissesRate + totalMutationsRate;
    }
    point.push(requests);
  }

  return series;
}

// prefix can be `compound_`, `prefix_`,
export function prependColumnNames(columns: string[], prefix: string) {
  return columns.map(column => {
    if (column === "time") {
      return column;
    }
    return `${prefix}${column}`;
  });
}
